import React from 'react'
import { compose, pure, withHandlers, withProps, defaultProps } from 'recompose'
import { identity } from 'ramda'
import { Field } from 'redux-form/immutable'
import { fromJS } from 'immutable'

import { createValidatorsFromProps } from '../ToReduxFormField/helpers'

/**
 * Takes a Redux-Form checkbox or radio and replaces the
 * onChange handler with one that will either return the
 * value of the original field, or undefined. It gets around
 * redux-form being stupid about "checkboxes and radios are ONLY bools"
 *
 * toReduxFormBool :: Pass in `type` `value` `onChange`
 *  and will return reduxForm `input` and `meta` and you can tell if it's checked by `checked` (not `input.check`)
 * @param {function} Component The input component to make better
 */
export const toReduxFormBoolField =
  Component =>
  ({ onChange, value, type, ...props }) => {
    const validatorFuncs = createValidatorsFromProps(props)
    return (
      <Field
        {...props}
        component={Component}
        // Bypassing `Field` (onChange, type, value)
        onChangeCallback={onChange}
        inputType={type}
        checkedValue={value}
        validate={validatorFuncs}
        validators={validatorFuncs}
      />
    )
  }

const toReduxFormBool = compose(
  toReduxFormBoolField,
  defaultProps({
    // set checkedValue `true` for checkbox without setting it's value
    checkedValue: true,
  }),
  withHandlers({
    handleChange:
      ({ input: { onChange }, onChangeCallback = identity, checkedValue }) =>
      ({ target: { checked } }) => {
        // value can sometimes be "false" or "" when the checked is true
        // and even if it's not, we return undefined for !checked anyway.
        const returnValue = checked ? checkedValue : false

        onChangeCallback(returnValue)
        onChange(returnValue)
      },
  }),
  withProps(({ input: { value }, checkedValue }) => {
    return {
      checked: JSON.stringify(fromJS(checkedValue)) === JSON.stringify(value),
    }
  }),
  pure,
)

export default toReduxFormBool
